/**
 * 编辑器命令通道 —— 把创作区工具栏**右侧那批按钮**搬到右侧图标栏所需的"跨组件接线"。
 *
 * ## 为什么需要它
 *
 * 用户要求："把编辑区域工具栏右侧的几个功能按钮移动到右侧栏中，
 * 用一个横杠与上方的功能页区分开。"
 *
 * 但按钮所在的位置（`PaneRail`，创作页层）与它们要操作的状态
 * （`showAppearancePanel` / `showFindPanel` / `showPreview`… 都在 `ChapterEditor` 里）
 * 不在同一层 —— 而且 `ChapterEditor` 是**状态的拥有者**，用 Context 反而要它向上注册。
 * 所以这里用一个极小的发布/订阅：编辑器发布命令与"当前是否展开"，右侧栏订阅并渲染按钮。
 *
 * ## 为什么可以是模块级单例
 *
 * 每个窗口同一时刻只有一个编辑器实例（副窗体只渲染单个数据页，**没有编辑器**，
 * 也就不会渲染图标栏）。所以单例安全，不需要 Context Provider 的样板。
 */

import { useSyncExternalStore } from 'react';

export interface EditorCommands {
  /** 编辑区外观（背景 / 稿纸行线 / 段落间距 / 首行缩进）面板是否展开 */
  appearanceOpen: boolean;
  toggleAppearance: () => void;
  /** 章节内查找替换是否展开 */
  findOpen: boolean;
  toggleFind: () => void;
  /** 章节摘要是否展开 */
  summaryOpen: boolean;
  toggleSummary: () => void;
  /** 打开取名工具弹窗 */
  openNameGenerator: () => void;
  /** 打开手机预览弹窗 */
  openPreview: () => void;
  /** 「历史版本」是否可用（没有章节或没有接线时置灰/不显示） */
  historyAvailable: boolean;
  openVersionHistory: () => void;
}

let current: EditorCommands | null = null;
const listeners = new Set<() => void>();

/** 编辑器在挂载与状态变化时发布（`ChapterEditor` 里的一个 effect） */
export function publishEditorCommands(next: EditorCommands | null): void {
  if (current === next) return;      // 同一个对象引用就不通知，避免无谓重渲染
  current = next;
  listeners.forEach((fn) => { fn(); });
}

export function getEditorCommands(): EditorCommands | null {
  return current;
}

function subscribe(fn: () => void): () => void {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}

/** 右侧栏用：拿到命令后渲染那批按钮（编辑器没挂载时返回 null → 按钮不渲染） */
export function useEditorCommands(): EditorCommands | null {
  return useSyncExternalStore(subscribe, getEditorCommands, getEditorCommands);
}
